import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import Swal from "sweetalert2";
import { axiosClient } from "../AxiosClient";
import { PRAMI_COLORS } from "../expedicionCertificados/certificadosTheme";
import {
  COMPONENT_LABELS,
  formatDate,
  formatMoney,
  roundMoney,
  squareFieldSx,
} from "./condonationFormat";

const ButtonAdd = styled(Button)({
  borderRadius: "20px !important",
  backgroundColor: "#161e55 !important",
  color: "#ffff !important",
  "&:hover": { backgroundColor: "#A8C8AF !important" },
});

// El saldo de cada concepto sale del `pending` del backend (ya neto de lo pagado via paymentApplication).
const COMPONENT_FIELDS = [
  { component: "GAC", pendingKey: "pendingGac" },
  { component: "MORA", pendingKey: "pendingMora" },
  { component: "INTERES_CORRIENTE", pendingKey: "pendingInterest" },
  { component: "FGA", pendingKey: "pendingFga" },
  { component: "SEGURO_VIDA", pendingKey: "pendingLifeInsurance" },
  { component: "SEGURO_DESEMPLEO", pendingKey: "pendingUnemploymentInsurance" },
  { component: "CAPITAL", pendingKey: "pendingCapital" },
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function CondonarQuotaDialog({ open, activeQuota, onClose, onSuccess }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setReason("");
  }, [open, activeQuota]);

  if (!activeQuota) {
    return null;
  }

  const quota = activeQuota.quota || {};
  const pending = activeQuota.pending || {};
  const payments = activeQuota.payments || [];

  // La condonacion siempre se asocia al ULTIMO pago aplicado (payments viene ordenado ascendente por
  // applicationDate); sin pagos, la condonacion es retroactiva.
  const lastPayment = payments.length > 0 ? payments[payments.length - 1] : null;
  const lastPaymentRecordUuid = lastPayment?.paymentRecordUuid || null;

  // Solo en este modal, la línea de Capital muestra el saldo capital total activo del crédito
  // (`activeCapital`), no la porción estructural de la cuota: condonar esta cuota arrastra todo el
  // capital vivo. El total se recalcula solo porque suma los componentes.
  const activeCapital = activeQuota.activeCapital;
  const pendingByComponent = COMPONENT_FIELDS.reduce((acc, field) => {
    const raw =
      field.component === "CAPITAL" && activeCapital != null
        ? activeCapital
        : pending[field.pendingKey];
    acc[field.component] = roundMoney(Math.max(0, toNumber(raw)));
    return acc;
  }, {});
  const totalPending = roundMoney(
    COMPONENT_FIELDS.reduce((sum, field) => sum + pendingByComponent[field.component], 0)
  );

  const submitCondonation = () => {
    setSubmitting(true);
    axiosClient
      .post(`/v1/condonations/quotas/${activeQuota.quota.uuid}`, {
        condoneAllPending: true,
        reason: reason.trim(),
        createdByUserUuid: localStorage.getItem("userUuid") || null,
        paymentRecordUuid: lastPaymentRecordUuid,
      })
      .then(() => {
        setSubmitting(false);
        onSuccess("Se condonó la cuota correctamente.");
        onClose();
      })
      .catch((error) => {
        setSubmitting(false);
        Swal.fire({
          icon: "error",
          title: "No se pudo condonar",
          text: error.response?.data?.message || "Ocurrió un error, intenta de nuevo.",
          confirmButtonColor: "#161e55",
        });
      });
  };

  const handleCondonar = () => {
    if (!reason.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Falta el motivo",
        text: "Debes indicar el motivo de la condonación.",
        confirmButtonColor: "#161e55",
      });
      return;
    }
    Swal.fire({
      icon: "warning",
      title: "¿Condonar y cerrar el crédito?",
      html:
        `Se condonarán <strong>todos los componentes</strong> de la cuota #${quota.quotaNumber ?? ""} ` +
        `más el <strong>saldo capital total activo</strong> del crédito. Todas las cuotas pendientes ` +
        `quedarán en <strong>PAID</strong> y el crédito se cerrará.<br/><br/>Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: "Sí, condonar todo",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#161e55",
      cancelButtonColor: "#9e9e9e",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        submitCondonation();
      }
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{`Condonar cuota #${quota.quotaNumber ?? ""}`}</DialogTitle>
      <DialogContent sx={squareFieldSx}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Se condonarán <strong>todos los componentes</strong> de esta cuota más el{" "}
          <strong>saldo capital total activo</strong> del crédito, cerrándolo. Las cuotas restantes
          quedarán en <strong>PAID</strong>. La condonación es total: no se puede condonar por concepto.
        </Typography>

        <Box
          sx={{
            mt: 0.5,
            mb: 2,
            p: 2,
            bgcolor: PRAMI_COLORS.pageBg,
            border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Saldo pendiente de la cuota (se condona completo)
          </Typography>
          <Typography variant="h6" sx={{ color: PRAMI_COLORS.primary, fontWeight: 700, lineHeight: 1.2 }}>
            {formatMoney(totalPending)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {lastPayment
              ? `Se aplicará sobre el último pago aplicado: ${formatDate(lastPayment.paymentDate)} · ${formatMoney(
                  lastPayment.paymentValue
                )}`
              : "La cuota no tiene pagos aplicados; la condonación será retroactiva."}
          </Typography>

          <Divider sx={{ my: 1.5, borderColor: PRAMI_COLORS.surfaceBorder }} />

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Saldo por componente
          </Typography>
          <Grid container spacing={1}>
            {COMPONENT_FIELDS.map((field) => (
              <Grid item xs={6} sm={4} key={field.component}>
                <Typography variant="caption" color="text.secondary">
                  {COMPONENT_LABELS[field.component] || field.component}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: PRAMI_COLORS.primary }}>
                  {formatMoney(pendingByComponent[field.component] || 0)}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Motivo"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <ButtonAdd disabled={submitting} onClick={handleCondonar}>
          Condonar todo y cerrar cuota
        </ButtonAdd>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}
